import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowRight, FaCheckCircle, FaRocket } from "react-icons/fa";
import { 
  LuBrainCircuit, LuLightbulb, LuSettings, LuBot, LuActivity, LuMap, LuPiggyBank, 
  LuScale, LuUsers, LuMessageSquare, LuTrendingUp, LuFileText, LuUserCog, LuEye, 
  LuGauge, LuRefreshCw, LuDollarSign, LuShieldCheck, LuCircleArrowUp, LuNetwork, 
  LuGitMerge, LuPlug, LuUserCheck, LuGlobe as LuGlobeIcon 
} from 'react-icons/lu';
import SEO from "../components/SEO/SEO";
import contentData from "../data/contentData.json";

// Icon mapping based on IDs
const iconMap = {
  // Categories
  'ai-strategy': <LuBrainCircuit />,
  'ai-solution-dev': <LuLightbulb />,
  'ai-managed-services': <LuSettings />,
  'agentic-ai': <LuBot />,

  // AI Strategy Services
  'ai-readiness': <LuActivity />,
  'enterprise-ai-roadmap': <LuMap />,
  'roi-modelling': <LuPiggyBank />,
  'ethical-ai': <LuScale />,
  'executive-workshops': <LuUsers />,

  // Solution Dev Services
  'intelligent-chatbots': <LuMessageSquare />,
  'predictive-analytics': <LuTrendingUp />,
  'document-intelligence': <LuFileText />,
  'personalization-engines': <LuUserCog />,
  'computer-vision-nlp': <LuEye />,

  // Managed Services
  'monitoring-tuning': <LuGauge />,
  'model-lifecycle': <LuRefreshCw />,
  'cost-optimization': <LuDollarSign />,
  'security-compliance': <LuShieldCheck />,
  'continuous-improvement': <LuCircleArrowUp />,

  // Agentic AI Services
  'multi-agent-architectures': <LuNetwork />,
  'autonomous-workflow': <LuGitMerge />,
  'enterprise-integration': <LuPlug />,
  'human-in-the-loop': <LuUserCheck />,
  'scalable-ecosystems': <LuGlobeIcon />
};

const getIcon = (id, fallback) => {
  return iconMap[id] || <span className="text-2xl">{fallback}</span>;
};

const AIServicePage = () => {
  const { id } = useParams();

  // Find the AI service category based on ID
  const serviceCategory = contentData.aiServices?.find((service) => service.id === id);

  if (!serviceCategory) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
        <Link to="/" className="text-blue-600 hover:text-blue-800">
          Go Home
        </Link>
      </div>
    );
  }

  const seoData = {
    title: `${serviceCategory.category} | Dhyon Technologies`,
    description: serviceCategory.description,
    keywords: `AI, Generative AI, ${serviceCategory.category}, Dhyon Technologies`,
  };

  return (
    <div className="bg-white font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO {...seoData} />

      {/* Hero Section */}
      <section className="relative pt-40 pb-20 bg-[#0F0524] overflow-hidden text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-900/40 via-[#0F0524] to-[#0F0524]"></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        
        <div className="container relative z-10 px-6 mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <div className="inline-block p-3 mb-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20">
                <span className="text-4xl text-purple-200">{getIcon(serviceCategory.id, serviceCategory.icon)}</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6">
                {serviceCategory.category}
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed font-light border-l-4 border-purple-500 pl-6">
                {serviceCategory.description}
              </p>
            </motion.div>
            
            {serviceCategory.image && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <img
                  src={serviceCategory.image}
                  alt={serviceCategory.category}
                  className="w-full h-auto rounded-2xl shadow-2xl border border-white/10"
                />
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Sub-Services Grid */}
      <section className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                Our Expertise
              </h2>
              <h3 className="text-xl md:text-2xl text-gray-600 mb-8 font-medium">
                What We Do
              </h3>
              <div className="h-1 w-24 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full"></div>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {serviceCategory.services.map((service, index) => {
              // Alternate gradient colors for visual variety
              const gradients = [
                'from-blue-50 to-cyan-50',
                'from-purple-50 to-pink-50',
                'from-indigo-50 to-blue-50',
                'from-green-50 to-emerald-50',
                'from-orange-50 to-amber-50'
              ];
              const borderColors = [
                'border-blue-200',
                'border-purple-200',
                'border-indigo-200',
                'border-green-200',
                'border-orange-200'
              ];
              const iconBgColors = [
                'bg-gradient-to-br from-blue-500 to-cyan-500',
                'bg-gradient-to-br from-purple-500 to-pink-500',
                'bg-gradient-to-br from-indigo-500 to-blue-500',
                'bg-gradient-to-br from-green-500 to-emerald-500',
                'bg-gradient-to-br from-orange-500 to-amber-500'
              ];
              const hoverShadows = [
                'hover:shadow-blue-200/50',
                'hover:shadow-purple-200/50',
                'hover:shadow-indigo-200/50',
                'hover:shadow-green-200/50',
                'hover:shadow-orange-200/50'
              ];

              const gradientIndex = index % gradients.length;
              
              return (
                <motion.div
                  key={service.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`
                    group relative bg-white rounded-2xl p-6 border-2 ${borderColors[gradientIndex]} 
                    hover:shadow-2xl ${hoverShadows[gradientIndex]} transition-all duration-500
                    hover:-translate-y-2 overflow-hidden
                  `}
                >
                  {/* Gradient Background Overlay on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradients[gradientIndex]} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`}></div>
                  
                  {/* Decorative Corner Element */}
                  <div className={`absolute top-0 right-0 w-32 h-32 ${iconBgColors[gradientIndex]} opacity-5 rounded-bl-full group-hover:opacity-10 transition-opacity duration-500`}></div>

                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="mb-5">
                      <div className={`
                        w-16 h-16 rounded-xl ${iconBgColors[gradientIndex]} 
                        flex items-center justify-center text-3xl shadow-lg
                        group-hover:scale-110 group-hover:rotate-3 transition-all duration-500
                      `}>
                        <span className="filter drop-shadow-lg">{getIcon(service.id, service.icon)}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors leading-tight">
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 mb-5 leading-relaxed text-sm line-clamp-4">
                      {service.description}
                    </p>
                    
                    {/* Outcome Badge */}
                    {service.outcome && (
                      <div className={`
                        bg-gradient-to-r ${gradients[gradientIndex]} 
                        border ${borderColors[gradientIndex]} rounded-xl p-4 
                        group-hover:shadow-lg transition-all duration-300
                      `}>
                        <div className="flex items-start gap-3">
                          <FaCheckCircle className={`text-lg mt-0.5 shrink-0 ${iconBgColors[gradientIndex].includes('blue') ? 'text-blue-600' : iconBgColors[gradientIndex].includes('purple') ? 'text-purple-600' : iconBgColors[gradientIndex].includes('green') ? 'text-green-600' : iconBgColors[gradientIndex].includes('orange') ? 'text-orange-600' : 'text-indigo-600'}`} />
                          <div className="flex-1">
                            <span className="text-xs font-bold uppercase tracking-wider block mb-1.5 opacity-70">
                              Outcome
                            </span>
                            <span className="text-sm font-semibold text-gray-800 leading-snug">
                              {service.outcome}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bottom Accent Line */}
                  <div className={`
                    absolute bottom-0 left-0 right-0 h-1 ${iconBgColors[gradientIndex]} 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-2xl
                  `}></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Overall Outcome Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="bg-gradient-to-r from-[#0F0524] to-[#1A1033] rounded-3xl p-10 md:p-16 shadow-2xl text-white flex flex-col md:flex-row items-center justify-between gap-12 border border-purple-900/30">
            <div className="md:w-2/3">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center border border-purple-500/50">
                   <FaRocket className="text-purple-400 text-xl" />
                </div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-purple-400">Overall Business Impact</h3>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
                {serviceCategory.overallOutcome}
              </h2>
            </div>
            <div className="md:w-1/3 flex justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-900 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-white/20"
              >
                Start Your Journey
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">
            Ready to scale your AI initiatives?
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Book a consultation with our AI experts to discuss your specific needs.
          </p>
          <div className="flex justify-center">
            <Link
              to="/contact"
              className="px-8 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition-colors shadow-lg hover:shadow-purple-200"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIServicePage;
